'use client';

import type { FC } from 'react';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type InputType = 'text' | 'email' | 'number' | 'password';

export type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  name?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  type?: InputType;
  icon?: React.ReactNode;
  errorMessage?: string;
  placeholder?: string;
  min?: number | string;
  form?: string;
  defaultValue?: number | string;
  onErrorColorizeBorder?: boolean;
  error?: boolean;
  disabled?: boolean;
  label?: string;
};

const FormInput: FC<Props> = forwardRef<HTMLInputElement, Props>(
  (
    {
      type = 'text',
      icon,
      errorMessage,
      min,
      form,
      defaultValue,
      onErrorColorizeBorder,
      error,
      label,
      disabled,
      ...props
    },
    ref,
  ) => (
    <>
      <div className={'mt-2'}>
        {label && (
          <label
            htmlFor={props.name}
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            {label}
          </label>
        )}
        <div className="relative rounded-md">
          {icon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex h-9 items-center pl-3">
              {icon}
            </div>
          )}
          <input
            id={props.name}
            className={twMerge(
              'block w-full rounded-full border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-brand-500 sm:text-sm sm:leading-6',
              error &&
                onErrorColorizeBorder &&
                'border-red-600 ring-red-600 focus:ring-red-600',
            )}
            type={type}
            {...props}
            ref={ref}
            {...(type === 'number' && { min })}
            {...(form && { form })}
            {...(defaultValue && { defaultValue })}
            {...(disabled && { disabled })}
          />
          {errorMessage && (
            <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
          )}
        </div>
      </div>
    </>
  ),
);

FormInput.displayName = 'FormInput';

export { FormInput };
